import Modal from "../../components/Modal/Modal"
import React, { useState, memo } from "react"
import Settings from "../Settings/Settings"
import Controls from "../Controls/Controls"
import ControlsSearch from "../Controls/ControlsSearch"
import ResultSet from "../ResultSet/ResultSet"
import seedCalculator, { get_roll, advanceFrame } from "./SeedCalculator"
import Info from "../../components/Info/Info"
import GearDatabaseList from "../GearDatabaseList/GearDatabaseList"
import Timeline from "../Timeline/Timeline"
import classes from "./SeedCalculator.module.css"
import { getBezierPath } from "reactflow"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMugHot as faMartiniGlassEmpty } from "@fortawesome/free-solid-svg-icons"
import Tabs from "../../components/Tabs/Tabs"
import ResultSetSearch from "../ResultSet/ResultSetSearch"
import {
    useLocalStorage,
    SkillIDs,
    drink_order,
    ability_order,
    MappingDrinkBrand,
    generate_abilities,
} from "../../Utils"
import { useTranslation } from "react-i18next"
import { defaultGearList } from "../../DefaultEntries"

const SeedChecker = props => {
    const [gearList, setGearList] = useLocalStorage("gearList", defaultGearList)
    const [loading, setLoading] = useState(false)
    const [currentSeed, setCurrentSeed] = useState("0x1")
    const [currentSeedTmp, setCurrentSeedTmp] = useState(currentSeed)
    const [currentStartFrame, setCurrentStartFrame] = useState(0)
    const [currentNumberOfFrames, setCurrentNumberOfFrames] = useState(10)
    const [displayResults, setDisplayResults] = useState([])
    const [nodes, setNodes] = useState([])
    const [edges, setEdges] = useState([])
    const [searchAbilities, setSearchAbilities] = useState([255, 255, 255])
    const [showAbilityControls, setAbilityControls] = useState(-1)
    const [displayTimeline, setDisplayTimeline] = useState(false)
    const [activeTab, setActiveTab] = useState(0)
    const [searchResult, setSearchResult] = useState([])
    const [allowChunks, setAllowChunks] = useState(false)
    const [fixedOrder, setFixedOrder] = useState(false)
    const { t } = useTranslation()
    const abilities = generate_abilities(t)
    const [currentBrand, setCurrentBrand] = useState({
        value: "B99",
        label: t("translation:CommonMsg.Gear.GearBrandName.B99"),
    })
    const [currentDrink, setCurrentDrink] = useState({
        value: "None",
        label: t("misc:Common.None"),
    })
    const [currentGear, setCurrentGear] = useState({ Label: t("Misc:Common.None") })

    const get_drink_labels = () => {
        const res = []
        for (const drink of drink_order) {
            res.push({
                value: MappingDrinkBrand[drink],
                label: t("translation:CommonMsg.Lobby.LobbyFoodName." + drink),
            })
        }
        return res
    }

    const [selectedDrinks, setSelectedDrinks] = useState(get_drink_labels())

    const ButtonEdge = memo(
        ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd, label }) => {
            const [edgePath] = getBezierPath({
                sourceX,
                sourceY,
                sourcePosition,
                targetX,
                targetY,
                targetPosition,
            })
            return (
                <>
                    <path id={id} style={style} className="react-flow__edge-path" d={edgePath} markerEnd={markerEnd} />
                    <foreignObject
                        width={50}
                        height={50}
                        x={targetX - 50}
                        y={targetY - 16}
                        className="edgebutton-foreignobject"
                        requiredExtensions="http://www.w3.org/1999/xhtml"
                    >
                        {label === "None" ? (
                            ""
                        ) : label === "Drink" ? (
                            <FontAwesomeIcon icon={faMartiniGlassEmpty} />
                        ) : (
                            <div style={{ display: "Flex", alignItems: "center" }}>
                                <FontAwesomeIcon icon={faMartiniGlassEmpty} />
                                <img
                                    src={`https://leanny.github.io/splat3/images/skill/${label}.png`}
                                    width={32}
                                    alt={label}
                                    className={classes.Ability}
                                />
                            </div>
                        )}
                    </foreignObject>
                </>
            )
        }
    )

    const edgeTypes = {
        buttonedge: ButtonEdge,
    }

    const brandChangeHandler = event => {
        setCurrentBrand(event)
    }

    const drinkChangeHandler = event => {
        setCurrentDrink(event)
    }

    const checkSeedHandler = event => {
        let inputstr = event.target.value.toUpperCase()
        if (inputstr.substring(0, 2) === "0X") {
            inputstr = inputstr.substring(2)
        }
        inputstr = inputstr.replace(/[^0-9A-F]/g, "")
        while (inputstr.length > 0 && inputstr.substring(0, 1) === "0") {
            inputstr = inputstr.slice(1)
        }
        if (inputstr.length === 0) {
            inputstr = "0"
        }
        inputstr = "0x" + inputstr
        inputstr = inputstr.substring(0, 10) // cut off too many characters
        setCurrentSeed(inputstr)
    }

    const checkStartFrameHandler = event => {
        let inputstr = event.target.value
        let negative = inputstr.substring(0, 1) === "-"
        inputstr = inputstr.replace(/[^0-9]/g, "")
        while (inputstr.length > 0 && inputstr.substring(0, 1) === "0") {
            inputstr = inputstr.slice(1)
        }
        if (inputstr.length === 0) {
            inputstr = "0"
        }
        if (negative) {
            inputstr = "-" + inputstr
        }

        inputstr = inputstr.substring(0, 5) // cut off too many characters
        setCurrentStartFrame(inputstr)
    }

    const checkNumberOfFrameHandler = event => {
        let inputstr = event.target.value
        inputstr = inputstr.replace(/[^0-9]/g, "")
        while (inputstr.length > 0 && inputstr.substring(0, 1) === "0") {
            inputstr = inputstr.slice(1)
        }
        if (inputstr.length === 0) {
            inputstr = "0"
        }
        inputstr = inputstr.substring(0, 5) // cut off too many characters
        setCurrentNumberOfFrames(inputstr)
    }

    const generateTimeline = () => {
        if (currentSeed === 0) return
        const start_x = 100
        const x_width = 600
        const y_width = 50
        const start_seed = advanceFrame(currentSeed, parseInt(currentStartFrame))
        const new_nodes = [
            {
                id: `${start_seed >>> 0}`,
                position: { x: start_x, y: ((1 + y_width) * abilities.length) / 2 },
                data: { label: `0x${(start_seed >>> 0).toString(16)}` },
                sourcePosition: "right",
                targetPosition: "left",
                type: "custom",
            },
        ]

        const new_edges = []
        let current_seed = start_seed
        for (let k = 1; k <= parseInt(currentNumberOfFrames); k++) {
            const [new_seed] = get_roll(current_seed, currentBrand.value, "None")
            new_nodes.push({
                id: `${new_seed >>> 0}`,
                position: { x: start_x + x_width * k, y: ((1 + y_width) * abilities.length) / 2 },
                data: { label: `0x${(new_seed >>> 0).toString(16)}` },
                sourcePosition: "right",
                targetPosition: "left",
                type: "custom",
            })
            current_seed = new_seed
        }
        const colors = [{ stroke: "blue" }, { stroke: "red" }]
        for (let k = 0; k < parseInt(currentNumberOfFrames); k++) {
            let i = 0
            current_seed = advanceFrame(start_seed, k)
            const [, , isDrink] = get_roll(current_seed, currentBrand.value, "MainInk_Save")
            if (isDrink) {
                const [new_seed, res] = get_roll(current_seed, currentBrand.value, "None")
                const ability_str = SkillIDs[res]

                new_nodes.push({
                    id: `${k}-None`,
                    position: {
                        x: start_x + x_width / 2 + x_width * k,
                        y: ((1 + y_width) * abilities.length) / 2 - y_width,
                    },
                    data: {
                        label: (
                            <img
                                src={`https://leanny.github.io/splat3/images/skill/${res}.png`}
                                width="32"
                                alt={ability_str}
                                className={classes.Ability}
                            />
                        ),
                    },
                    sourcePosition: "right",
                    targetPosition: "left",
                    type: "custom",
                })
                new_nodes.push({
                    id: `${k}-Drink`,
                    position: {
                        x: start_x + x_width / 2 + x_width * k,
                        y: ((1 + y_width) * abilities.length) / 2 + y_width,
                    },
                    data: {
                        label: <FontAwesomeIcon icon={faMartiniGlassEmpty} />,
                    },
                    sourcePosition: "right",
                    targetPosition: "left",
                    type: "custom",
                })

                new_edges.push({
                    id: `${current_seed >>> 0}-${k}-None`,
                    source: `${current_seed >>> 0}`,
                    target: `${k}-None`,
                    style: colors[k % 2],
                    label: "None",
                    type: "buttonedge",
                })
                new_edges.push({
                    id: `${current_seed >>> 0}-${k}-Drink`,
                    source: `${current_seed >>> 0}`,
                    target: `${k}-Drink`,
                    style: colors[k % 2],
                    label: "Drink",
                    type: "buttonedge",
                })

                new_edges.push({
                    id: `${k}-None-${new_seed >>> 0}`,
                    target: `${new_seed >>> 0}`,
                    source: `${k}-None`,
                    style: colors[k % 2],
                })

                new_edges.push({
                    id: `${k}-Drink-${new_seed >>> 0}`,
                    target: `${new_seed >>> 0}`,
                    source: `${k}-Drink`,
                    style: colors[k % 2],
                })
            } else {
                for (const ability of abilities) {
                    const [new_seed, res] = get_roll(current_seed, currentBrand.value, ability.value)
                    i += 1
                    const ability_str = SkillIDs[res]
                    const url = `https://leanny.github.io/splat3/images/skill/${res}.png`

                    new_nodes.push({
                        id: `${k}-${ability.value}`,
                        position: { x: start_x + x_width / 2 + x_width * k, y: y_width * i },
                        data: { label: <img src={url} width="32" alt={ability_str} className={classes.Ability} /> },
                        sourcePosition: "right",
                        targetPosition: "left",
                        type: "custom",
                    })
                    new_edges.push({
                        id: `${current_seed >>> 0}-${k}-${ability.value}`,
                        source: `${current_seed >>> 0}`,
                        target: `${k}-${ability.value}`,
                        style: colors[k % 2],
                        label: ability.value,
                        type: "buttonedge",
                    })
                    new_edges.push({
                        id: `${k}-${ability.value}-${new_seed >>> 0}`,
                        target: `${new_seed >>> 0}`,
                        source: `${k}-${ability.value}`,
                        style: colors[k % 2],
                    })
                }
            }
        }

        setNodes(new_nodes)
        setEdges(new_edges)
    }

    const calculateResultHandler = () => {
        setLoading(false)
        const res = seedCalculator(
            currentSeed,
            parseInt(currentStartFrame),
            parseInt(currentNumberOfFrames),
            currentBrand.value,
            currentDrink.value,
            t
        )
        setDisplayResults(res)
        if (displayTimeline) {
            generateTimeline()
        }
    }

    const updateSeedHandler = (newSeed, storeRes) => {
        setCurrentSeed(newSeed)
        if (storeRes) {
            const original = { ...currentGear.OriginalEntry }
            original.RandomContext = parseInt(newSeed.substring(2), 16)
            const gearKey =
                currentGear.Type === 0
                    ? "HaveGearHeadMap"
                    : currentGear.Type === 1
                    ? "HaveGearClothesMap"
                    : "HaveGearShoesMap"
            const new_dict = { ...gearList }
            new_dict[gearKey][currentGear.Id] = original
            setGearList(new_dict)
        }
    }

    const gearChangeHandler = newGear => {
        setCurrentBrand({
            value: newGear.value.Brand,
            label: t("translation:CommonMsg.Gear.GearBrandName." + newGear.value.Brand),
        })
        if (newGear.Seed > 0) {
            setCurrentSeed(`0x${(newGear.Seed >>> 0).toString(16)}`)
        }
        setCurrentGear(newGear)
        setSearchResult([])
    }

    const filterFunction = val => {
        return val.RandomContext > 0
    }

    const timelineToggle = () => {
        setDisplayTimeline(!displayTimeline)
        if (!displayTimeline) {
            generateTimeline()
        }
    }

    const changeTabHandler = val => {
        setActiveTab(val)
        setSearchResult([])
    }

    const sort3 = arr => {
        if (fixedOrder) {
            return arr
        }
        const sorted_res = [0, 0, 0]
        if (arr[0] < arr[1]) {
            if (arr[1] > arr[2]) {
                if (arr[0] < arr[2]) {
                    sorted_res[0] = arr[0]
                    sorted_res[1] = arr[2]
                    sorted_res[2] = arr[1]
                } else {
                    sorted_res[0] = arr[2]
                    sorted_res[1] = arr[0]
                    sorted_res[2] = arr[1]
                }
            } else {
                sorted_res[0] = arr[0]
                sorted_res[1] = arr[1]
                sorted_res[2] = arr[2]
            }
        } else {
            if (arr[1] < arr[2]) {
                if (arr[0] < arr[2]) {
                    sorted_res[0] = arr[1]
                    sorted_res[1] = arr[0]
                    sorted_res[2] = arr[2]
                } else {
                    sorted_res[0] = arr[1]
                    sorted_res[1] = arr[2]
                    sorted_res[2] = arr[0]
                }
            } else {
                sorted_res[0] = arr[2]
                sorted_res[1] = arr[1]
                sorted_res[2] = arr[0]
            }
        }
        return sorted_res
    }

    const find_optimal_path = (current_seed, sorted_res) => {
        if (sorted_res[2] !== 255) {
            return find_optimal_path3(current_seed, sorted_res)
        }
        if (sorted_res[2] === 255 && sorted_res[1] !== 255) {
            return find_optimal_path2(current_seed, sorted_res)
        }
        if (sorted_res[1] === 255 && sorted_res[0] !== 255) {
            return find_optimal_path1(current_seed, sorted_res)
        }
    }

    const find_optimal_path_chunks3 = (current_seed, sorted_res) => {
        if (sorted_res[2] !== 255) {
            const potential_res = []
            let res = find_optimal_path2(current_seed, [sorted_res[1], sorted_res[2], 255])
            if (res !== undefined) {
                if (fixedOrder) {
                    potential_res.push([
                        ["Unknown", res[0][0], res[0][1]],
                        ["Unknown", res[1][0], res[1][1]],
                        [ability_order[sorted_res[0]], "Unknown", "Unknown"],
                    ])
                } else {
                    potential_res.push([
                        ["Unknown", res[0][0], res[0][1]],
                        ["Unknown", res[1][0], res[1][1]],
                        [ability_order[sorted_res[0]], "Unknown", "Unknown"],
                    ])
                }
            }
            res = find_optimal_path2(current_seed, [sorted_res[0], sorted_res[2], 255])
            if (res !== undefined) {
                if (fixedOrder) {
                    potential_res.push([
                        [res[0][0], "Unknown", res[0][1]],
                        [res[1][0], "Unknown", res[1][1]],
                        ["Unknown", ability_order[sorted_res[1]], "Unknown"],
                    ])
                } else {
                    potential_res.push([
                        ["Unknown", res[0][0], res[0][1]],
                        ["Unknown", res[1][0], res[1][1]],
                        [ability_order[sorted_res[1]], "Unknown", "Unknown"],
                    ])
                }
            }
            res = find_optimal_path2(current_seed, [sorted_res[0], sorted_res[1], 255])
            if (res !== undefined) {
                if (fixedOrder) {
                    potential_res.push([
                        [res[0][0], res[0][1], "Unknown"],
                        [res[1][0], res[1][1], "Unknown"],
                        ["Unknown", "Unknown", ability_order[sorted_res[2]]],
                    ])
                } else {
                    potential_res.push([
                        ["Unknown", res[0][0], res[0][1]],
                        ["Unknown", res[1][0], res[1][1]],
                        [ability_order[sorted_res[2]], "Unknown", "Unknown"],
                    ])
                }
            }

            potential_res.sort((a, b) => new Set(a[0]).size > new Set(b[0]).size)
            if (potential_res.length > 0) {
                return potential_res[0]
            }
        }
    }

    const find_optimal_path_chunks3_replace = (current_seed, sorted_res) => {
        if (sorted_res[2] === 255) return
        const potential_res = []
        for (const a1 of ability_order) {
            let res
            if (!fixedOrder) {
                if (SkillIDs[a1] !== sorted_res[0]) {
                    res = find_optimal_path3(current_seed, sort3([sorted_res[1], SkillIDs[a1], sorted_res[2]]))
                    if (res !== undefined && res[1][1] === a1) {
                        potential_res.push([
                            [res[0][0], res[0][1], res[0][2]],
                            [res[1][0], res[1][1], res[1][2]],
                            ["Unknown", ability_order[sorted_res[0]], "Unknown"],
                        ])
                    }
                }
            }
            if (SkillIDs[a1] !== sorted_res[1]) {
                res = find_optimal_path3(current_seed, sort3([sorted_res[0], SkillIDs[a1], sorted_res[2]]))
                if (res !== undefined && res[1][1] === a1) {
                    potential_res.push([
                        [res[0][0], res[0][1], res[0][2]],
                        [res[1][0], res[1][1], res[1][2]],
                        ["Unknown", ability_order[sorted_res[1]], "Unknown"],
                    ])
                }
            }
            if (SkillIDs[a1] !== sorted_res[2]) {
                res = find_optimal_path3(current_seed, sort3([sorted_res[0], SkillIDs[a1], sorted_res[1]]))
                if (res !== undefined && res[1][1] === a1) {
                    potential_res.push([
                        [res[0][0], res[0][1], res[0][2]],
                        [res[1][0], res[1][1], res[1][2]],
                        ["Unknown", ability_order[sorted_res[2]], "Unknown"],
                    ])
                }
            }
        }
        potential_res.sort((a, b) => new Set(a[0]).size > new Set(b[0]).size)
        if (potential_res.length > 0) {
            return potential_res[0]
        }
    }

    const find_optimal_path_chunks2 = (current_seed, sorted_res) => {
        if (sorted_res[2] !== 255) {
            let res
            res = find_optimal_path1(current_seed, [sorted_res[2], 255, 255])
            if (res !== undefined) {
                if (fixedOrder) {
                    return [
                        ["Unknown", "Unknown", res[0][0]],
                        ["Unknown", "Unknown", res[1][0]],
                        [ability_order[sorted_res[0]], ability_order[sorted_res[1]], "Unknown"],
                    ]
                } else {
                    return [
                        ["Unknown", "Unknown", res[0][0]],
                        ["Unknown", "Unknown", res[1][0]],
                        [ability_order[sorted_res[0]], ability_order[sorted_res[1]], "Unknown"],
                    ]
                }
            }
            res = find_optimal_path1(current_seed, [sorted_res[1], 255, 255])
            if (res !== undefined) {
                if (fixedOrder) {
                    return [
                        ["Unknown", res[0][0], "Unknown"],
                        ["Unknown", res[1][0], "Unknown"],
                        [ability_order[sorted_res[0]], "Unknown", ability_order[sorted_res[2]]],
                    ]
                } else {
                    return [
                        ["Unknown", "Unknown", res[0][0]],
                        ["Unknown", "Unknown", res[1][0]],
                        [ability_order[sorted_res[0]], ability_order[sorted_res[2]], "Unknown"],
                    ]
                }
            }
            res = find_optimal_path1(current_seed, [sorted_res[0], 255, 255])
            if (res !== undefined) {
                if (fixedOrder) {
                    return [
                        [res[0][0], "Unknown", "Unknown"],
                        [res[1][0], "Unknown", "Unknown"],
                        ["Unknown", ability_order[sorted_res[1]], ability_order[sorted_res[2]]],
                    ]
                } else {
                    return [
                        ["Unknown", "Unknown", res[0][0]],
                        ["Unknown", "Unknown", res[1][0]],
                        [ability_order[sorted_res[1]], ability_order[sorted_res[2]], "Unknown"],
                    ]
                }
            }
        }

        if (sorted_res[2] === 255 && sorted_res[1] !== 255) {
            let res = find_optimal_path1(current_seed, [sorted_res[0], 255, 255])
            if (res !== undefined) {
                if (fixedOrder && ability_order[sorted_res[0]] === res[1][0]) {
                    return [
                        [res[0][0], "Unknown", "Unknown"],
                        [res[1][0], "Unknown", "Unknown"],
                        ["Unknown", ability_order[sorted_res[1]], "Unknown"],
                    ]
                } else {
                    return [
                        ["Unknown", res[0][0], "Unknown"],
                        ["Unknown", res[1][0], "Unknown"],
                        [ability_order[sorted_res[1]], "Unknown", "Unknown"],
                    ]
                }
            }
            res = find_optimal_path1(current_seed, [sorted_res[1], 255, 255])
            if (res !== undefined) {
                if (fixedOrder && ability_order[sorted_res[0]] === res[1][0]) {
                    return [
                        [res[0][0], "Unknown", "Unknown"],
                        [res[1][0], "Unknown", "Unknown"],
                        ["Unknown", ability_order[sorted_res[0]], "Unknown"],
                    ]
                } else {
                    return [
                        ["Unknown", res[0][0], "Unknown"],
                        ["Unknown", res[1][0], "Unknown"],
                        [ability_order[sorted_res[0]], "Unknown", "Unknown"],
                    ]
                }
            }
        }
    }

    const find_optimal_path_chunks1 = (current_seed, sorted_res) => {
        if (sorted_res[1] === 255 && sorted_res[0] !== 255) {
            return [
                ["Unknown", "Unknown", "Unknown"],
                ["Unknown", "Unknown", "Unknown"],
                [ability_order[sorted_res[0]], "Unknown", "Unknown"],
            ]
        }
    }

    const ability_in_order = () => {
        const res = ["None"]
        for (const elem of selectedDrinks) {
            res.push(elem.value)
        }
        return res
    }

    const cartesian = (...a) => a.reduce((a, b) => a.flatMap(d => b.map(e => [d, e].flat())))

    const find_optimal_path3 = (current_seed, sorted_res) => {
        const all_abilities = []
        for (const c of ability_in_order()) {
            all_abilities.push([c, c, c])
        }
        for (const c of ability_in_order()) {
            for (const d of ability_in_order()) {
                all_abilities.push([c, c, d])
                all_abilities.push([c, d, c])
                all_abilities.push([d, c, c])
            }
        }
        all_abilities.push(...cartesian(ability_in_order(), ability_in_order(), ability_in_order()))
        for (const [a1, a2, a3] of all_abilities) {
            const [seed2, res1] = get_roll(current_seed, currentBrand.value, a1)
            const [seed3, res2] = get_roll(seed2, currentBrand.value, a2)
            const [, res3] = get_roll(seed3, currentBrand.value, a3)
            const t1 = SkillIDs[res1]
            const t2 = SkillIDs[res2]
            const t3 = SkillIDs[res3]
            const to_check = sort3([t1, t2, t3])
            if (arraysEqual(to_check, sorted_res)) {
                return [
                    [a1, a2, a3],
                    [res1, res2, res3],
                    ["Unknown", "Unknown", "Unknown"],
                ]
            }
        }
    }

    const find_optimal_path2 = (current_seed, sorted_res) => {
        const all_abilities = []
        for (const c of ability_in_order()) {
            all_abilities.push([c, c])
        }
        for (const c of ability_in_order()) {
            all_abilities.push([c, "None"])
            all_abilities.push([c, "None"])
        }
        all_abilities.push(...cartesian(ability_in_order(), ability_in_order()))
        for (const [a1, a2] of all_abilities) {
            const [seed2, res1] = get_roll(current_seed, currentBrand.value, a1)
            const [, res2] = get_roll(seed2, currentBrand.value, a2)
            const t1 = SkillIDs[res1]
            const t2 = SkillIDs[res2]
            const to_check = sort3([t1, t2, 255])
            if (arraysEqual(to_check, sorted_res)) {
                return [
                    [a1, a2, "Unknown"],
                    [res1, res2, "Unknown"],
                    ["Unknown", "Unknown", "Unknown"],
                ]
            }
        }
    }

    const find_optimal_path1 = (current_seed, sorted_res) => {
        for (const a1 of ability_in_order()) {
            const [, res1] = get_roll(current_seed, currentBrand.value, a1)
            const t1 = SkillIDs[res1]
            if (t1 === sorted_res[0])
                return [
                    [a1, "Unknown", "Unknown"],
                    [res1, "Unknown", "Unknown"],
                    ["Unknown", "Unknown", "Unknown"],
                ]
        }
    }

    function arraysEqual(a, b) {
        if (a === b) return true
        if (a == null || b == null) return false
        if (a.length !== b.length) return false

        for (var i = 0; i < a.length; ++i) {
            if (a[i] !== b[i]) return false
        }
        return true
    }

    function move_unknown_back(arr) {
        let unknowns = 0
        let res = []
        for (let a of arr) {
            if (a === 255) {
                unknowns += 1
            } else {
                res.push(a)
            }
        }
        for (let i = 0; i < unknowns; ++i) {
            res.push(255)
        }
        return res
    }

    const findSearchHandler = () => {
        if (currentSeed === 0) return
        let start_seed = advanceFrame(currentSeed >>> 0, currentStartFrame)
        const sorted_res = move_unknown_back(sort3(searchAbilities))
        setSearchResult([])
        const res = []
        for (let i = 0; i < currentNumberOfFrames; i++) {
            const r = find_optimal_path(start_seed, sorted_res)
            if (r === undefined) {
                start_seed = advanceFrame(start_seed, 1)
            } else {
                res.push([start_seed, r, i])
                break
            }
        }
        if (allowChunks) {
            start_seed = advanceFrame(currentSeed >>> 0, currentStartFrame)
            for (let i = 0; i < currentNumberOfFrames; i++) {
                const r = find_optimal_path_chunks3_replace(start_seed, sorted_res)
                if (r === undefined) {
                    start_seed = advanceFrame(start_seed, 1)
                } else {
                    res.push([start_seed, r, i])
                    break
                }
            }
            start_seed = advanceFrame(currentSeed >>> 0, currentStartFrame)
            for (let i = 0; i < currentNumberOfFrames; i++) {
                const r = find_optimal_path_chunks3(start_seed, sorted_res)
                if (r === undefined) {
                    start_seed = advanceFrame(start_seed, 1)
                } else {
                    res.push([start_seed, r, i])
                    break
                }
            }
            start_seed = advanceFrame(currentSeed >>> 0, currentStartFrame)
            for (let i = 0; i < currentNumberOfFrames; i++) {
                const r = find_optimal_path_chunks2(start_seed, sorted_res)
                if (r === undefined) {
                    start_seed = advanceFrame(start_seed, 1)
                } else {
                    res.push([start_seed, r, i])
                    break
                }
            }
            start_seed = advanceFrame(currentSeed >>> 0, currentStartFrame)
            for (let i = 0; i < currentNumberOfFrames; i++) {
                const r = find_optimal_path_chunks1(start_seed, sorted_res)
                if (r === undefined) {
                    start_seed = advanceFrame(start_seed, 1)
                } else {
                    res.push([start_seed, r, i])
                    break
                }
            }
        }
        setCurrentSeedTmp(currentSeed)
        setSearchResult(res)
    }

    const handleAbilityChange = (idx, ability) => {
        setSearchAbilities(currentAbilities => {
            const copy_ability = [...currentAbilities]
            copy_ability[idx] = ability
            return copy_ability
        })
        setAbilityControls(i => ((i + 2) % 4) - 1)
    }

    const handleAbilityControl = idx => {
        setAbilityControls(idx)
    }

    const handleDrinkSelection = e => {
        setSelectedDrinks(e)
    }

    const allowChunksHandler = () => {
        setAllowChunks(currentValue => !currentValue)
    }

    const fixedOrderHandler = () => {
        setFixedOrder(currentValue => !currentValue)
    }

    return (
        <>
            <Modal show={loading} />
            <h1>Splatoon 3 - {t("misc:Navigation.GearSeedChecker")}</h1>
            <Info />
            <GearDatabaseList
                currentGear={currentGear}
                gearChange={gearChangeHandler}
                filterFunction={filterFunction}
            />
            <Settings
                currentBrand={currentBrand}
                brandChange={brandChangeHandler}
                currentDrink={currentDrink}
                drinkChange={drinkChangeHandler}
            />
            <Tabs clicked={changeTabHandler} active={activeTab} />
            {activeTab === 0 ? (
                <>
                    <Controls
                        seedValidator={checkSeedHandler}
                        currentSeed={currentSeed}
                        currentStartFrame={currentStartFrame}
                        currentNumberOfFrames={currentNumberOfFrames}
                        startValidator={checkStartFrameHandler}
                        numberValidator={checkNumberOfFrameHandler}
                        calculateResults={calculateResultHandler}
                        timelineHandler={timelineToggle}
                        showTimeline={displayTimeline}
                    />
                    <ResultSet results={displayResults} updateSeed={updateSeedHandler} currentGear={currentGear} />
                    {displayTimeline ? (
                        <>
                            <h2>{t("misc:Checker.Timeline")}</h2>
                            <div className={classes.Timeline}>
                                <Timeline nodes={nodes} edges={edges} edgeTypes={edgeTypes} theme={props.theme} />
                            </div>
                        </>
                    ) : null}
                </>
            ) : (
                <>
                    <ControlsSearch
                        seedValidator={checkSeedHandler}
                        currentSeed={currentSeed}
                        currentStartFrame={currentStartFrame}
                        currentNumberOfFrames={currentNumberOfFrames}
                        startValidator={checkStartFrameHandler}
                        numberValidator={checkNumberOfFrameHandler}
                        calculateResults={findSearchHandler}
                        selectedAbilities={searchAbilities}
                        showAbilityControls={showAbilityControls}
                        abilityChange={handleAbilityChange}
                        abilityControlChange={handleAbilityControl}
                        handleDrinkSelection={handleDrinkSelection}
                        selectedDrinks={selectedDrinks}
                        allowChunks={allowChunks}
                        allowChunksHandler={allowChunksHandler}
                        fixedOrder={fixedOrder}
                        fixedOrderHandler={fixedOrderHandler}
                    />
                    <ResultSetSearch
                        results={searchResult}
                        currentSeed={currentSeedTmp}
                        currentBrand={currentBrand.value}
                    />
                </>
            )}
        </>
    )
}

export default SeedChecker
